import { CheckboxProps } from "@mui/material/Checkbox/Checkbox";
import { RadioProps } from "@mui/material/Radio/Radio";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  ButtonBase as MuiButtonBase,
  Grid,
  Paper,
  useControlled,
  useFormControl
} from "@mui/material";
import React, { MouseEventHandler, ReactElement } from "react";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import { grey } from "@mui/material/colors";

export interface SmallPaperSelectionControlProps {
  content?: ReactElement;
  control: typeof Radio | typeof Checkbox;
  autoFocus?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  required?: boolean;
  tabIndex?: number;
  value?: unknown;
  controlProps?: CheckboxProps | RadioProps;
  error?: string;
  hiddenCheckbox?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

interface SmallPaperSelectionControlStylesProps extends SmallPaperSelectionControlProps {
  checked?: boolean;
}

const useStyles = makeStyles<SmallPaperSelectionControlStylesProps>()((theme, props) => {
  return {
    root: {
      width: "100%",
      position: "relative",
      borderRadius: (theme.shape.borderRadius as number) * 2.5,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      "&:active": {
        color: theme.palette.secondary.main,
        "& $paper": {
          borderColor: theme.palette.secondary.main
        }
      }
    },
    paper: {
      padding: theme.spacing(2, 2.5),
      backgroundColor: grey[100],
      borderRadius: (theme.shape.borderRadius as number) * 2.5,
      width: "100%",
      borderWidth: "3px",
      borderStyle: "solid",
      borderColor:
        props.checked && !props.disabled
          ? theme.palette.primary.main
          : props.checked && props.disabled
            ? theme.palette.secondary.main
            : theme.palette.common.white,
      transition: `border-color 0.1s ${theme.transitions.easing.easeInOut}`,
      backgroundClip: "padding-box"
    },
    label: {
      flexGrow: 1,
      color: theme.palette.text.secondary
    },
    control: {
      display: props.hiddenCheckbox ? "none" : "inherit",
      "&.Mui-checked": {
        color: !props.disabled ? theme.palette.primary.main : theme.palette.secondary.main
      }
    }
  };
});
const useErrorStyles = makeStyles()(({ palette }) => ({
  text: {
    color: palette.error.main
  }
}));
export const SmallPaperSelectionControl: React.FC<
  React.PropsWithChildren<SmallPaperSelectionControlProps>
> = (props) => {
  const {
    control,
    autoFocus,
    checked: checkedProp,
    defaultChecked,
    disabled: disabledProp,
    name,
    onChange,
    required,
    tabIndex,
    value,
    content,
    error,
    onClick
  } = props;
  const [checked, setCheckedState] = useControlled({
    controlled: checkedProp,
    default: Boolean(defaultChecked),
    name: "PaperSelectionControl",
    state: "checked"
  });
  const { classes: errorStyle } = useErrorStyles();

  const controlRef = React.useRef<HTMLInputElement | null>(null);

  const muiFormControl = useFormControl();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Workaround for https://github.com/facebook/react/issues/9023
    if (event.nativeEvent.defaultPrevented) {
      return;
    }

    const newChecked = event.target.checked;

    setCheckedState(newChecked);
    onChange?.(event, newChecked);
  };

  let disabled = disabledProp;

  if (muiFormControl) {
    if (typeof disabled === "undefined") {
      disabled = muiFormControl.disabled;
    }
  }

  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    onClick?.(event);
    if (controlRef.current) {
      controlRef.current.click();
    }
  };

  const { classes } = useStyles(props);
  const controlProps = {
    ...props.controlProps,
    autoFocus,
    disabled,
    name,
    required,
    tabIndex,
    checked,
    value,
    className: classes.control,
    onChange: handleInputChange,
    onClick: (e: any) => {
      e.stopPropagation();
    },
    inputRef: controlRef,
    disableRipple: true
  };
  return (
    <>
      <MuiButtonBase
        focusRipple
        disabled={props.disabled}
        className={classes.root}
        onClick={handleButtonClick}
      >
        <Paper elevation={0} className={classes.paper}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid
              item
              xs={props.hiddenCheckbox ? 12 : "auto"}
              maxWidth={props.hiddenCheckbox ? "100%" : "80%"}
            >
              {content}
            </Grid>
            <Grid item>{React.createElement(control, controlProps)}</Grid>
          </Grid>
        </Paper>
      </MuiButtonBase>
      {error && (
        <Box pl={1} className={errorStyle.text}>
          {error}
        </Box>
      )}
    </>
  );
};
